import { useEffect, useRef, useCallback, useState } from 'react';
import { io, Socket } from 'socket.io-client';
// import { getApiUrl } from '../config'; // No se usa actualmente
import { logger } from '../utils/logger';

interface MatchUpdateEvent {
  type: string;
  matchId: string;
  data: any;
  timestamp: string;
}

interface UseWebSocketReturn {
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;
  joinMatch: (matchId: string) => void;
  leaveMatch: (matchId: string) => void;
  occupySpace: (matchId: string, spaceKey: string) => void;
  releaseSpace: (matchId: string, spaceKey: string) => void;
  addReaction: (matchId: string, reviewId: string, emoji: string, userId: string, userName: string) => void;
  removeReaction: (matchId: string, reviewId: string, emoji: string, userId: string) => void;
  joinNotifications: (userId: string) => void;
  leaveNotifications: (userId: string) => void;
  disconnect: () => void;
}

export const useWebSocket = (
  onMatchUpdate?: (event: MatchUpdateEvent) => void,
  onError?: (error: string) => void
): UseWebSocketReturn => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const currentMatchRef = useRef<string | null>(null);
  const onMatchUpdateRef = useRef(onMatchUpdate);
  const onErrorRef = useRef(onError);

  const getSocketUrl = () => {
    if (process.env.NODE_ENV === 'development') {
      return '';
    }
    
    const apiBaseUrl = process.env.REACT_APP_API_URL;
    if (!apiBaseUrl) {
      // REACT_APP_API_URL not configured
      return '';
    }
    
    return apiBaseUrl;
  };

  const connect = useCallback(() => {
    if (socketRef.current?.connected) {
      return;
    }

    setIsConnecting(true);
    setConnectionError(null);

    try {
      const socketUrl = getSocketUrl();
      logger.websocket('Conectando a WebSocket:', socketUrl);
      
      socketRef.current = io(socketUrl, {
        transports: ['websocket', 'polling'],
        timeout: 20000,
        forceNew: true,
        auth: {
          token: localStorage.getItem('authToken')
        }
      });

      socketRef.current.on('connect', () => {
        logger.websocket('WebSocket conectado:', socketRef.current?.id);
        setIsConnected(true);
        setIsConnecting(false);
        setConnectionError(null);

        if (currentMatchRef.current) {
          socketRef.current?.emit('joinMatch', currentMatchRef.current);
        }
      });

      socketRef.current.on('disconnect', () => {
        logger.websocket('WebSocket desconectado');
        setIsConnected(false);
        setIsConnecting(false);
      });

      socketRef.current.on('connect_error', (error) => {
        logger.error('Error de conexión WebSocket:', error);
        setIsConnecting(false);
        setConnectionError(error.message || 'Error de conexión');
        // Los errores de WebSocket solo van a consola, no se muestran al usuario
      });

      socketRef.current.on('matchUpdate', (event: MatchUpdateEvent) => {
        logger.websocket('Evento WebSocket recibido:', event);
        onMatchUpdateRef.current?.(event);
      });

      // Escuchar eventos de notificaciones
      socketRef.current.on('notification', (data: any) => {
        logger.websocket('Notificación WebSocket recibida:', data);
        // Disparar evento personalizado para el contador de notificaciones
        window.dispatchEvent(new CustomEvent('websocket-notification', { detail: data }));
      });

      socketRef.current.on('notificationCountUpdate', (data: any) => {
        logger.websocket('Actualización de contador WebSocket recibida:', data);
        // Disparar evento personalizado para el contador de notificaciones
        window.dispatchEvent(new CustomEvent('websocket-notification', { detail: data }));
      });

    } catch (error) {
      logger.error('Error al inicializar WebSocket:', error);
      setIsConnecting(false);
      setConnectionError('Error al inicializar conexión');
      // Los errores de WebSocket solo van a consola, no se muestran al usuario
    }
  }, []); // Sin dependencias para evitar el loop

  const joinMatch = useCallback((matchId: string) => {
    currentMatchRef.current = matchId;
    if (socketRef.current?.connected) {
      logger.websocket('Uniéndose al partido:', matchId);
      socketRef.current.emit('joinMatch', matchId);
    }
  }, []);

  const leaveMatch = useCallback((matchId: string) => {
    if (currentMatchRef.current === matchId) {
      currentMatchRef.current = null;
    }
    if (socketRef.current?.connected) {
      logger.websocket('Dejando el partido:', matchId);
      socketRef.current.emit('leaveMatch', matchId);
    }
  }, []);

  const occupySpace = useCallback((matchId: string, spaceKey: string) => {
    if (socketRef.current?.connected) {
      logger.websocket('Ocupando espacio:', { matchId, spaceKey });
      socketRef.current.emit('occupySpace', { matchId, spaceKey });
    }
  }, []);

  const releaseSpace = useCallback((matchId: string, spaceKey: string) => {
    if (socketRef.current?.connected) {
      logger.websocket('Liberando espacio:', { matchId, spaceKey });
      socketRef.current.emit('releaseSpace', { matchId, spaceKey });
    }
  }, []);

  const addReaction = useCallback((matchId: string, reviewId: string, emoji: string, userId: string, userName: string) => {
    if (socketRef.current?.connected) {
      logger.websocket('Agregando reacción:', { matchId, reviewId, emoji, userId });
      socketRef.current.emit('addReaction', { matchId, reviewId, emoji, userId, userName });
    }
  }, []);

  const removeReaction = useCallback((matchId: string, reviewId: string, emoji: string, userId: string) => {
    if (socketRef.current?.connected) {
      logger.websocket('Removiendo reacción:', { matchId, reviewId, emoji, userId });
      socketRef.current.emit('removeReaction', { matchId, reviewId, emoji, userId });
    }
  }, []);

  const joinNotifications = useCallback((userId: string) => {
    if (socketRef.current?.connected) {
      logger.websocket('Uniéndose a notificaciones del usuario:', userId);
      socketRef.current.emit('joinNotifications', userId);
    }
  }, []);

  const leaveNotifications = useCallback((userId: string) => {
    if (socketRef.current?.connected) {
      logger.websocket('Saliendo de notificaciones del usuario:', userId);
      socketRef.current.emit('leaveNotifications', userId);
    }
  }, []);

  const disconnect = useCallback(() => {
    currentMatchRef.current = null;
    
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    
    setIsConnected(false);
    setIsConnecting(false);
    setConnectionError(null);
  }, []);

  // Actualizar referencias cuando cambien los callbacks
  useEffect(() => {
    onMatchUpdateRef.current = onMatchUpdate;
  }, [onMatchUpdate]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    connect();
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    isConnected,
    isConnecting,
    connectionError,
    joinMatch,
    leaveMatch,
    occupySpace,
    releaseSpace,
    addReaction,
    removeReaction,
    joinNotifications,
    leaveNotifications,
    disconnect
  };
}; 